(() => {
  if (window.FoldingLinkDemoVideos) return;

  const initDemoSwitcher = (root) => {
    if (root.dataset.foldinglinkAppDemosInit === 'true') return;

    const tabs = Array.from(root.querySelectorAll('[data-foldinglink-app-demo-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-foldinglink-app-demo-panel]'));
    const selectorRail = root.querySelector('.foldinglink-app-demo-videos__selectors');
    const indicator = root.querySelector('[data-foldinglink-app-demo-indicator]');
    if (!tabs.length || !panels.length) return;

    root.dataset.foldinglinkAppDemosInit = 'true';
    root.classList.add('is-enhanced');

    const abortController = new AbortController();
    const options = { signal: abortController.signal };
    const passiveOptions = { passive: true, signal: abortController.signal };
    const mobileQuery = window.matchMedia('(max-width: 767px)');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const initialTab = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];
    let activeId = initialTab.dataset.foldinglinkAppDemoTab;

    const positionIndicator = (tab) => {
      if (!mobileQuery.matches || !tab || !indicator) return;
      indicator.style.width = `${tab.offsetWidth}px`;
      indicator.style.transform = `translate3d(${tab.offsetLeft}px, 0, 0)`;
    };

    const revealSelectedTab = (tab) => {
      if (!mobileQuery.matches || !selectorRail || !tab) return;
      const maxScroll = Math.max(0, selectorRail.scrollWidth - selectorRail.clientWidth);
      if (maxScroll < 2) return;
      const visibleLeft = selectorRail.scrollLeft + 8;
      const visibleRight = selectorRail.scrollLeft + selectorRail.clientWidth - 8;
      const tabLeft = tab.offsetLeft;
      const tabRight = tabLeft + tab.offsetWidth;
      if (tabLeft >= visibleLeft && tabRight <= visibleRight) return;
      const target = Math.min(maxScroll, Math.max(0, tabLeft - ((selectorRail.clientWidth - tab.offsetWidth) / 2)));
      selectorRail.scrollTo({ left: target, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    };

    const activate = (id, moveFocus = false) => {
      activeId = id;
      let selectedTab;

      tabs.forEach((tab) => {
        const selected = tab.dataset.foldinglinkAppDemoTab === id;
        tab.classList.toggle('is-active', selected);
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        if (selected) selectedTab = tab;
      });

      if (mobileQuery.matches) {
        panels.forEach((panel) => {
          const selected = panel.dataset.foldinglinkAppDemoPanel === id;
          const owningTab = tabs.find((tab) => tab.dataset.foldinglinkAppDemoTab === panel.dataset.foldinglinkAppDemoPanel);
          panel.classList.toggle('is-active', selected);
          panel.hidden = !selected;
          panel.setAttribute('role', 'tabpanel');
          if (owningTab?.id) panel.setAttribute('aria-labelledby', owningTab.id);

          if (!selected) panel.querySelectorAll('video').forEach((video) => video.pause());
        });

        requestAnimationFrame(() => {
          positionIndicator(selectedTab);
          revealSelectedTab(selectedTab);
        });
      }

      if (moveFocus) selectedTab?.focus();
    };

    const syncResponsiveMode = () => {
      if (mobileQuery.matches) {
        activate(activeId);
        return;
      }

      if (indicator) {
        indicator.style.width = '0px';
        indicator.style.transform = 'translate3d(0, 0, 0)';
      }

      panels.forEach((panel) => {
        panel.hidden = false;
        panel.classList.remove('is-active');
        panel.removeAttribute('role');
        panel.removeAttribute('aria-labelledby');
      });
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(tab.dataset.foldinglinkAppDemoTab), options);
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

        event.preventDefault();
        let nextIndex = index;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        activate(tabs[nextIndex].dataset.foldinglinkAppDemoTab, true);
      }, options);
    });

    root.foldinglinkSelectDemo = activate;

    const handleViewportResize = () => {
      if (!mobileQuery.matches) return;
      const selectedTab = tabs.find((tab) => tab.dataset.foldinglinkAppDemoTab === activeId);
      requestAnimationFrame(() => positionIndicator(selectedTab));
    };

    mobileQuery.addEventListener?.('change', syncResponsiveMode, options);
    window.addEventListener('resize', handleViewportResize, passiveOptions);

    let resizeObserver;
    if (selectorRail && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(handleViewportResize);
      resizeObserver.observe(selectorRail);
    }

    root.foldinglinkDestroyDemo = () => {
      abortController.abort();
      resizeObserver?.disconnect();
      root.classList.remove('is-enhanced');
      delete root.dataset.foldinglinkAppDemosInit;
      delete root.foldinglinkSelectDemo;
      delete root.foldinglinkDestroyDemo;
    };

    syncResponsiveMode();
  };

  const init = (scope = document) => {
    const roots = [];
    if (scope instanceof Element && scope.matches('[data-foldinglink-app-demos]')) roots.push(scope);
    roots.push(...scope.querySelectorAll('[data-foldinglink-app-demos]'));
    roots.forEach(initDemoSwitcher);
  };

  window.FoldingLinkDemoVideos = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.('[data-foldinglink-app-demos]')
      ? event.target
      : event.target.querySelector?.('[data-foldinglink-app-demos]');
    root?.foldinglinkDestroyDemo?.();
  });
  document.addEventListener('shopify:block:select', (event) => {
    const blockId = event.detail?.blockId;
    if (!blockId) return;

    const root = event.target.closest?.('[data-foldinglink-app-demos]')
      || event.target.querySelector?.('[data-foldinglink-app-demos]');
    root?.foldinglinkSelectDemo?.(blockId);
  });
})();
